import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Skeleton,
  Chip,
  Alert,
  Divider,
  Button
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SecurityIcon from "@mui/icons-material/Security";
import { Link as RouterLink } from "react-router-dom";
import { get } from "aws-amplify/api";
import Moment from "moment";
import { useAuth } from "../libs/AuthContext";
import LicenseKeyCard from "../components/LicenseKeyCard";

const MONO_STACK = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';

function Row({ label, value, mono = false, last = false }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 0.25, sm: 3 }}
      sx={{
        py: 1.75,
        borderBottom: last ? 0 : 1,
        borderColor: "divider",
        alignItems: { sm: "baseline" }
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          minWidth: { sm: 200 },
          flexShrink: 0
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: mono ? MONO_STACK : undefined,
          fontSize: "0.95rem",
          fontWeight: 500,
          wordBreak: "break-word",
          color: "text.primary"
        }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

export default function Home() {
  const { isAuthenticated, isAdminUser, isLicenseManager } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [license, setLicense] = useState(null);
  const [error, setError] = useState("");
  // null = unknown (still loading or not a licence-manager); true/false otherwise.
  const [mfaEnrolled, setMfaEnrolled] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        const op = get({ apiName: "license", path: "/license" });
        const { body } = await op.response;
        let data = null;
        try {
          data = await body.json();
        } catch (_) {
          // Malformed JSON from the API gateway integration template.
          // Treat as "no licence" rather than surface a cryptic
          // "Unexpected token" alert.
        }
        // The /license success template emits empty strings for missing
        // fields when the lambda hits an error path — treat that the
        // same as "no licence assigned".
        if (data && data.licenseId) {
          setLicense(data);
        }
      } catch (e) {
        // Network/auth failure. License-manager-only users hit this if
        // their /license call fails entirely; that's expected.
        if (!isLicenseManager) {
          setError(e?.message ?? "Failed to load license");
        }
      } finally {
        setIsLoading(false);
      }
    })();

    // For license-managers, check enrolment via /step-up/status so we
    // can prompt them to set up MFA before their first edit hits a
    // step-up wall. Tracked in our own DDB rather than via
    // Cognito's UserMFASettingList — see step_up_setup_complete.
    if (isLicenseManager) {
      (async () => {
        try {
          const op = get({ apiName: "license", path: "/step-up/status" });
          const { body } = await op.response;
          const data = await body.json();
          setMfaEnrolled(Boolean(data?.enrolled));
        } catch (_) {
          // Best-effort — don't break Home if the check fails.
          setMfaEnrolled(null);
        }
      })();
    }
  }, [isAuthenticated, isLicenseManager]);

  const showMfaPrompt = isLicenseManager && mfaEnrolled === false;

  const expiry = license?.licenseEndDate ? Moment(license.licenseEndDate) : null;
  const isExpired = expiry ? expiry.isBefore(Moment()) : false;
  const expiringSoon = expiry ? !isExpired && expiry.diff(Moment(), "days") < 30 : false;

  const mfaPromptAlert = showMfaPrompt ? (
    <Alert
      severity="warning"
      icon={<SecurityIcon />}
      sx={{ mb: 3 }}
      action={
        <Button
          component={RouterLink}
          to="/admin/setup-mfa"
          color="inherit"
          size="small"
          variant="outlined"
        >
          Set up MFA
        </Button>
      }
    >
      <strong>MFA isn't set up yet.</strong> You'll need an authenticator app to edit licences or create organisations. Takes about 30 seconds.
    </Alert>
  ) : null;

  // License-manager-only: no licence loaded, no error, but the user is
  // a member of absolute.license-manager. Show a landing card pointing
  // them at the management pages.
  if (!isLoading && !license && !error && isLicenseManager) {
    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            Welcome
          </Typography>
          <Chip label="Licence manager" size="small" color="primary" icon={<AdminPanelSettingsIcon />} />
        </Stack>

        {mfaPromptAlert}

        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              You're signed in as a licence manager.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You don't have a personal DataStar licence — your role is to manage
              client organisations: extending licences, adjusting user counts,
              email-domain mappings, and provisioning new organisations.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                component={RouterLink}
                to="/admin/licenses"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                Licences
              </Button>
              <Button
                component={RouterLink}
                to="/admin/organizations/new"
                variant="outlined"
              >
                New organisation
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // No licence and not a licence manager — they're authenticated but
  // their account isn't set up. Friendlier than a blank page.
  if (!isLoading && !license && !error && !isLicenseManager) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 3 }}>
          Your DataStar licence
        </Typography>
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No licence assigned to your account yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If this is unexpected, ask your administrator to register you
              under your organisation's licence, or contact{" "}
              <Box component="a" href="mailto:sales@absolute.technology" sx={{ color: "inherit" }}>
                sales@absolute.technology
              </Box>.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
          Your DataStar licence
        </Typography>
        {isAdminUser && <Chip label="Admin" size="small" color="secondary" />}
        {isLicenseManager && <Chip label="Licence manager" size="small" color="primary" />}
      </Stack>

      {mfaPromptAlert}

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      {isLoading ? (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={240} height={32} />
              <Skeleton variant="text" width={120} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
            </Stack>
          </CardContent>
        </Card>
      ) : license ? (
        <Stack spacing={3}>
          <Card>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: { sm: "flex-start" }, mb: 3 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.25 }}
                  >
                    {license.licensedTo ?? "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {license.category ?? "—"} licence
                  </Typography>
                </Box>
                {expiry && (
                  <Chip
                    icon={!isExpired && !expiringSoon ? <VerifiedIcon /> : undefined}
                    label={
                      isExpired
                        ? `Expired ${expiry.format("MMM D, YYYY")}`
                        : `Expires ${expiry.format("MMM D, YYYY")}`
                    }
                    color={isExpired ? "error" : expiringSoon ? "warning" : "success"}
                    variant={isExpired || expiringSoon ? "filled" : "outlined"}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                )}
              </Stack>

              <Divider />

              <Box>
                <Row label="Licence ID" value={license.licenseId} mono />
                <Row label="Email" value={license.email} />
                {isAdminUser ? (
                  <>
                    <Row label="Assigned licences" value={license.assignedLicenses} />
                    <Row label="Allocated licences" value={license.allocatedLicenses} last />
                  </>
                ) : (
                  <Row label="Licence type" value={license.category} last />
                )}
              </Box>
            </CardContent>
          </Card>

          {license.cmdlineLicenseKey && (
            <LicenseKeyCard
              licenseKey={license.cmdlineLicenseKey}
              licenseId={license.licenseId}
            />
          )}
        </Stack>
      ) : null}
    </Box>
  );
}
